import {
  BODY_FONTS,
  DISPLAY_FONTS,
  OUTPUT_DIMENSIONS,
  PALETTES
} from "../constants";
import type { RenderResult, StudioState } from "../types";
import { createRandom, randomBetween } from "./random";

const TAU = Math.PI * 2;

export function loopOscillation(progress: number, phase = 0): number {
  return Math.sin(TAU * progress + phase);
}

type CanvasLike = HTMLCanvasElement | OffscreenCanvas;

interface RenderOptions {
  progress?: number;
  pixelRatio?: number;
}

interface TextLayout {
  lines: string[];
  fontSize: number;
  lineHeight: number;
  overflow: boolean;
}

function createCanvas(width: number, height: number): CanvasLike {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(width, height);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function getContext(canvas: CanvasLike): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D {
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D is not available.");
  return context as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
}

function hexToRgba(hex: string, alpha: number): string {
  const value = hex.replace("#", "");
  const red = Number.parseInt(value.slice(0, 2), 16);
  const green = Number.parseInt(value.slice(2, 4), 16);
  const blue = Number.parseInt(value.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function drawMesh(
  context: CanvasRenderingContext2D,
  state: StudioState,
  progress: number,
  width: number,
  height: number
): void {
  const palette = PALETTES[state.style.palette].colors;
  const random = createRandom(state.seed ^ 0x91ae3d2b);

  for (let index = 0; index < 9; index += 1) {
    const baseX = randomBetween(random, -0.1, 1.1);
    const baseY = randomBetween(random, -0.12, 1.12);
    const phase = random() * TAU;
    const drift = state.animation.colorDrift;
    const x = (baseX + Math.sin(TAU * progress + phase) * 0.07 * drift) * width;
    const y = (baseY + Math.cos(TAU * progress + phase) * 0.06 * drift) * height;
    const radius = randomBetween(random, 0.28, 0.62) * Math.max(width, height);
    const gradient = context.createRadialGradient(x, y, 0, x, y, radius);
    const color = palette[index % palette.length];
    gradient.addColorStop(0, hexToRgba(color, 0.72));
    gradient.addColorStop(0.52, hexToRgba(color, 0.25));
    gradient.addColorStop(1, hexToRgba(color, 0));
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
  }
}

function drawBokeh(
  context: CanvasRenderingContext2D,
  state: StudioState,
  progress: number,
  width: number,
  height: number,
  count = 20
): void {
  const palette = PALETTES[state.style.palette].colors;
  const random = createRandom(state.seed ^ 0x45fe1189);
  const intensity = state.style.bokeh;

  context.save();
  context.globalCompositeOperation = "screen";
  for (let index = 0; index < count; index += 1) {
    const phase = random() * TAU;
    const baseX = randomBetween(random, -0.08, 1.08);
    const baseY = randomBetween(random, -0.08, 1.08);
    const drift = state.animation.bokehDrift;
    const x = (baseX + Math.sin(TAU * progress + phase) * 0.045 * drift) * width;
    const y = (baseY + Math.cos(TAU * progress + phase) * 0.04 * drift) * height;
    const radius = randomBetween(random, 0.025, 0.14) * Math.min(width, height);
    const color = palette[(index + 2) % palette.length];
    const gradient = context.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, hexToRgba(color, 0.22 * intensity));
    gradient.addColorStop(0.28, hexToRgba(color, 0.13 * intensity));
    gradient.addColorStop(1, hexToRgba(color, 0));
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(x, y, radius, 0, TAU);
    context.fill();
  }
  context.restore();
}

function drawArches(
  context: CanvasRenderingContext2D,
  state: StudioState,
  progress: number,
  width: number,
  height: number
): void {
  const palette = PALETTES[state.style.palette].colors;
  const random = createRandom(state.seed ^ 0x77d99831);
  const scale = Math.min(width, height);

  context.save();
  context.globalCompositeOperation = "soft-light";
  for (let index = 0; index < 5; index += 1) {
    const phase = random() * TAU;
    const drift = state.animation.shapeDrift;
    const x =
      randomBetween(random, -0.18, 0.78) * width +
      Math.sin(TAU * progress + phase) * scale * 0.045 * drift;
    const y =
      randomBetween(random, -0.15, 0.74) * height +
      Math.cos(TAU * progress + phase) * scale * 0.035 * drift;
    const archWidth = randomBetween(random, 0.34, 0.8) * scale;
    const archHeight = randomBetween(random, 0.48, 1.05) * scale;
    const radius = Math.min(archWidth * 0.48, archHeight * randomBetween(random, 0.22, 0.48));

    context.fillStyle = hexToRgba(palette[(index + 1) % palette.length], 0.22);
    context.beginPath();
    context.roundRect(x, y, archWidth, archHeight, [radius, radius, radius * 0.32, radius * 0.32]);
    context.fill();
  }
  context.restore();
}

function drawLightColumns(
  context: CanvasRenderingContext2D,
  state: StudioState,
  progress: number,
  width: number,
  height: number
): void {
  const palette = PALETTES[state.style.palette];
  const random = createRandom(state.seed ^ 0x37a1f095);

  context.save();
  context.globalCompositeOperation = "screen";
  for (let column = 0; column < 18; column += 1) {
    const phase = random() * TAU;
    const drift = state.animation.shapeDrift;
    const x =
      ((column + 0.5) / 18) * width +
      Math.sin(TAU * progress + phase) * width * 0.012 * drift;
    const columnWidth = randomBetween(random, 0.006, 0.024) * width;
    const segmentHeight = randomBetween(random, 0.06, 0.18) * height;
    const gap = randomBetween(random, 0.025, 0.07) * height;
    context.fillStyle = hexToRgba(
      column % 4 === 0 ? palette.signal : palette.colors[(column + 1) % 4],
      0.035 + state.style.variance * 0.055
    );
    for (let y = -segmentHeight; y < height + segmentHeight; y += segmentHeight + gap) {
      const offset = Math.cos(TAU * progress + phase) * segmentHeight * 0.28 * drift;
      context.fillRect(x - columnWidth / 2, y + offset, columnWidth, segmentHeight);
    }
  }
  context.restore();
}

function drawOrbitalHalo(
  context: CanvasRenderingContext2D,
  state: StudioState,
  progress: number,
  width: number,
  height: number
): void {
  const palette = PALETTES[state.style.palette];
  const centerX = state.output === "wide" ? width * 0.72 : width * 0.52;
  const centerY = height * 0.46;
  const scale = Math.min(width, height);
  const rotation = progress * TAU * state.animation.shapeDrift;

  context.save();
  context.translate(centerX, centerY);
  context.rotate(rotation);
  context.globalCompositeOperation = "screen";
  for (let index = 0; index < 8; index += 1) {
    const radiusX = scale * (0.12 + index * 0.052);
    const radiusY = radiusX * (0.58 + index * 0.025);
    context.strokeStyle = hexToRgba(
      index % 2 === 0 ? palette.signal : palette.colors[(index + 2) % 4],
      0.18 - index * 0.012
    );
    context.lineWidth = Math.max(1, scale * (0.007 - index * 0.0005));
    context.beginPath();
    context.ellipse(0, 0, radiusX, radiusY, index * 0.19, 0, TAU);
    context.stroke();
  }
  context.restore();
}

function drawVignette(
  context: CanvasRenderingContext2D,
  strength: number,
  width: number,
  height: number
): void {
  if (strength <= 0) return;
  const gradient = context.createRadialGradient(
    width * 0.5,
    height * 0.44,
    Math.min(width, height) * 0.12,
    width * 0.5,
    height * 0.5,
    Math.max(width, height) * 0.72
  );
  gradient.addColorStop(0, "rgba(0,0,0,0)");
  gradient.addColorStop(1, `rgba(8,7,6,${Math.min(0.7, strength)})`);
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);
}

function drawGrain(
  context: CanvasRenderingContext2D,
  state: StudioState,
  progress: number,
  width: number,
  height: number
): void {
  if (state.style.grain <= 0) return;
  const grainSize = 128;
  const grainCanvas = createCanvas(grainSize, grainSize);
  const grainContext = getContext(grainCanvas);
  const image = grainContext.createImageData(grainSize, grainSize);
  const frame = Math.floor(progress * 60) % 60;
  const random = createRandom(state.seed ^ frame ^ 0xb16b00b5);
  for (let index = 0; index < image.data.length; index += 4) {
    const value = Math.floor(random() * 255);
    image.data[index] = value;
    image.data[index + 1] = value;
    image.data[index + 2] = value;
    image.data[index + 3] = Math.floor(255 * state.style.grain);
  }
  grainContext.putImageData(image, 0, 0);
  context.save();
  context.globalCompositeOperation = "soft-light";
  context.imageSmoothingEnabled = false;
  const pattern = context.createPattern(grainCanvas as CanvasImageSource, "repeat");
  if (pattern) {
    context.fillStyle = pattern;
    context.fillRect(0, 0, width, height);
  }
  context.restore();
}

function drawBackground(
  context: CanvasRenderingContext2D,
  state: StudioState,
  progress: number,
  width: number,
  height: number
): void {
  const palette = PALETTES[state.style.palette];
  context.fillStyle = palette.colors[0];
  context.fillRect(0, 0, width, height);

  context.save();
  context.filter = `saturate(${state.style.vibrance}) brightness(${state.style.brightness})`;
  drawMesh(context, state, progress, width, height);
  switch (state.style.motif) {
    case "mesh":
      drawBokeh(context, state, progress, width, height, 9);
      break;
    case "bokeh":
      drawBokeh(context, state, progress, width, height, 28);
      break;
    case "arches":
      drawArches(context, state, progress, width, height);
      drawBokeh(context, state, progress, width, height, 10);
      break;
    case "light-columns":
      drawLightColumns(context, state, progress, width, height);
      break;
    case "orbital-halo":
      drawOrbitalHalo(context, state, progress, width, height);
      drawBokeh(context, state, progress, width, height, 8);
      break;
  }
  context.restore();
  drawVignette(context, state.style.vignette, width, height);
  drawGrain(context, state, progress, width, height);
}

export function fitHeadline(
  context: CanvasRenderingContext2D,
  headline: string,
  maxWidth: number,
  maxHeight: number,
  startingSize: number,
  fontFamily = '"Staatliches", sans-serif',
  lineHeightRatio = 0.92
): TextLayout {
  const minimumSize = Math.max(32, startingSize * 0.52);

  for (let fontSize = startingSize; fontSize >= minimumSize; fontSize -= 2) {
    context.font = `${fontSize}px ${fontFamily}`;
    const lines: string[] = [];
    const explicitLines = headline.trim().split(/\n+/);

    explicitLines.forEach((explicitLine) => {
      const words = explicitLine.trim().split(/\s+/).filter(Boolean);
      let current = "";
      words.forEach((word) => {
        const candidate = current ? `${current} ${word}` : word;
        if (context.measureText(candidate).width <= maxWidth || !current) current = candidate;
        else {
          lines.push(current);
          current = word;
        }
      });
      if (current) lines.push(current);
    });

    const lineHeight = fontSize * lineHeightRatio;
    if (lines.length <= 4 && lines.length * lineHeight <= maxHeight) {
      return { lines, fontSize, lineHeight, overflow: false };
    }
  }

  context.font = `${minimumSize}px ${fontFamily}`;
  const fallback = headline.trim().split(/\s+/).slice(0, 16).join(" ");
  return {
    lines: [fallback],
    fontSize: minimumSize,
    lineHeight: minimumSize * lineHeightRatio,
    overflow: true
  };
}

function sampleLuminance(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
): number {
  try {
    const sampleCanvas = createCanvas(8, 8);
    const sampleContext = getContext(sampleCanvas);
    sampleContext.drawImage(context.canvas as CanvasImageSource, x, y, width, height, 0, 0, 8, 8);
    const pixels = sampleContext.getImageData(0, 0, 8, 8).data;
    let total = 0;
    for (let index = 0; index < pixels.length; index += 4) {
      const red = pixels[index] / 255;
      const green = pixels[index + 1] / 255;
      const blue = pixels[index + 2] / 255;
      total += 0.2126 * red + 0.7152 * green + 0.0722 * blue;
    }
    return total / 64;
  } catch {
    return 0.35;
  }
}

function drawText(
  context: CanvasRenderingContext2D,
  state: StudioState,
  width: number,
  height: number
): RenderResult {
  const palette = PALETTES[state.style.palette];
  const displayFont = DISPLAY_FONTS[state.typography.displayFont];
  const bodyFont = BODY_FONTS[state.typography.bodyFont];
  const blockWidth = width * state.text.width;
  const anchorX = width * state.text.positionX;
  const blockTop = height * state.text.positionY;
  const useDarkInk = state.text.tone === "dark" || state.style.palette === "bone";
  const ink = useDarkInk ? "#171310" : palette.ink;
  const alignment = state.text.align;
  const x =
    alignment === "left"
      ? anchorX
      : alignment === "right"
        ? anchorX
        : anchorX;
  const left =
    alignment === "left" ? x : alignment === "right" ? x - blockWidth : x - blockWidth / 2;
  const sampledLuminance = sampleLuminance(
    context,
    Math.max(0, left - width * 0.03),
    Math.max(0, blockTop - height * 0.04),
    Math.min(width - Math.max(0, left), blockWidth + width * 0.06),
    height * 0.55
  );
  const contrastNeeded =
    (!useDarkInk && sampledLuminance > 0.38) || (useDarkInk && sampledLuminance < 0.6);

  if (contrastNeeded) {
    const shadeCanvas = createCanvas(width, height);
    const shadeContext = getContext(shadeCanvas);
    const shadeLeft = Math.max(0, left - width * 0.025);
    const shadeTop = Math.max(0, blockTop - height * 0.025);
    const shadeWidth = Math.min(width - shadeLeft, blockWidth + width * 0.05);
    const shadeHeight = Math.min(height - shadeTop, height * 0.48);
    shadeContext.filter = `blur(${Math.max(24, height * 0.035)}px)`;
    shadeContext.fillStyle = useDarkInk ? "rgba(244,237,226,0.48)" : "rgba(7,6,5,0.48)";
    shadeContext.beginPath();
    shadeContext.roundRect(
      shadeLeft + width * 0.015,
      shadeTop + height * 0.015,
      Math.max(1, shadeWidth - width * 0.03),
      Math.max(1, shadeHeight - height * 0.03),
      height * 0.035
    );
    shadeContext.fill();
    context.drawImage(shadeCanvas as CanvasImageSource, 0, 0);
  }

  context.save();
  context.textAlign = alignment;
  context.textBaseline = "alphabetic";
  context.fillStyle = ink;

  let y = blockTop;
  if (state.text.eyebrow) {
    const eyebrowSize = Math.max(16, height * 0.021);
    context.font = `700 ${eyebrowSize}px ${bodyFont.family}`;
    context.letterSpacing = `${eyebrowSize * 0.13}px`;
    context.fillStyle = state.style.palette === "bone" ? palette.signal : "#f3a16b";
    context.fillText(state.text.eyebrow.toUpperCase(), x, y + eyebrowSize);
    y += eyebrowSize + height * 0.05;
  }

  const layout = fitHeadline(
    context,
    state.text.headline,
    blockWidth,
    height * 0.32,
    state.output === "wide" ? height * 0.125 : width * 0.115,
    displayFont.family,
    displayFont.lineHeight
  );
  context.fillStyle = ink;
  context.font = `${layout.fontSize}px ${displayFont.family}`;
  context.letterSpacing = `${layout.fontSize * displayFont.tracking}px`;
  layout.lines.forEach((line) => {
    y += layout.lineHeight;
    context.fillText(line.toUpperCase(), x, y);
  });

  if (state.text.subline) {
    y += height * 0.052;
    const sublineSize = Math.max(18, height * 0.026);
    context.font = `400 ${sublineSize}px ${bodyFont.family}`;
    context.letterSpacing = "0px";
    context.fillStyle = hexToRgba(ink, 0.78);
    const subline = state.text.subline.slice(0, 220);
    context.fillText(subline, x, y, blockWidth);
  }

  if (state.text.cta) {
    y += height * 0.073;
    const ctaSize = Math.max(15, height * 0.019);
    context.font = `700 ${ctaSize}px ${bodyFont.family}`;
    context.letterSpacing = `${ctaSize * 0.08}px`;
    context.fillStyle = ink;
    context.fillText(state.text.cta.toUpperCase(), x, y);
    const measured = context.measureText(state.text.cta.toUpperCase()).width;
    let ruleStart = x;
    if (alignment === "center") ruleStart = x - measured / 2;
    if (alignment === "right") ruleStart = x - measured;
    context.fillStyle = palette.signal;
    context.fillRect(ruleStart, y + height * 0.012, measured, Math.max(2, height * 0.003));
  }
  context.restore();

  return {
    overflow: layout.overflow,
    headlineLines: layout.lines,
    headlineSize: layout.fontSize,
    sampledLuminance
  };
}

export function renderStudioFrame(
  context: CanvasRenderingContext2D,
  state: StudioState,
  options: RenderOptions = {}
): RenderResult {
  const width = context.canvas.width;
  const height = context.canvas.height;
  const progress = ((options.progress ?? 0) % 1 + 1) % 1;

  context.clearRect(0, 0, width, height);
  drawBackground(context, state, progress, width, height);
  return drawText(context, state, width, height);
}

export function createOutputCanvas(
  state: StudioState,
  dimensions = OUTPUT_DIMENSIONS[state.output]
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;
  return canvas;
}
